import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, BookCopy, BookOpen, Clock, Coins, Library as LibraryIcon, Tags, Trophy, Users } from "lucide-react";

import { libraryService } from "../services/libraryService";
import { usePermissions } from "../hooks/usePermissions";
import { AuthorsCategoriesPanel } from "../components/library/AuthorsCategoriesPanel";
import { CataloguePanel, CopiesPanel } from "../components/library/CataloguePanel";
import { FinesPanel, LoansPanel, ReservationsPanel } from "../components/library/CirculationPanels";
import { ProgramsPanel } from "../components/library/ProgramsPanel";
import { isLate, isOpenReservation, isOut, money, unpaidTotal } from "../components/library/libraryLogic";

type Tab = "catalogue" | "copies" | "loans" | "reservations" | "fines" | "programmes" | "setup";
const TABS: { id: Tab; staff: string; reader: string; icon: typeof BookOpen; staffOnly?: boolean }[] = [
  { id: "catalogue", staff: "Catalogue", reader: "Catalogue", icon: BookOpen },
  { id: "copies", staff: "Copies", reader: "", icon: BookCopy, staffOnly: true },
  { id: "loans", staff: "Loans", reader: "Books out", icon: Users },
  { id: "reservations", staff: "Reservations", reader: "Reservations", icon: Clock },
  { id: "fines", staff: "Fines", reader: "Fines", icon: AlertCircle },
  { id: "programmes", staff: "Reading programmes", reader: "Reading programmes", icon: Trophy },
  { id: "setup", staff: "Authors & categories", reader: "", icon: Tags, staffOnly: true },
];

/** The school library: the catalogue, lending, reservations, fines and reading programmes. */
export default function Library() {
  const { hasCapability, hasRole } = usePermissions();
  const canManage = hasCapability("access.library.librarian");
  const canWaive = hasCapability("access.library.leadership");
  // Pupils reserve for themselves and parents for their children; staff who
  // are not library staff (teachers) may reserve for any pupil.
  const canReserve = !canManage;
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter((item) => canManage || !item.staffOnly);
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "catalogue";
  const view = params.get("view") ?? undefined;
  const [openBook, setOpenBook] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const books = useQuery({ queryKey: ["library-books"], queryFn: libraryService.getBooks });
  const copies = useQuery({ queryKey: ["book-copies"], queryFn: libraryService.getCopies });
  const loans = useQuery({ queryKey: ["library-borrowings"], queryFn: libraryService.getBorrowings });
  const reservations = useQuery({ queryKey: ["library-reservations"], queryFn: libraryService.getReservations });
  const fines = useQuery({ queryKey: ["library-fines"], queryFn: libraryService.getFines });
  const programs = useQuery({ queryKey: ["library-programs"], queryFn: libraryService.getReadingPrograms });
  const progress = useQuery({ queryKey: ["library-progress"], queryFn: libraryService.getReadingProgress });
  const students = useQuery({ queryKey: ["library-students"], queryFn: libraryService.getStudents });
  const authors = useQuery({ queryKey: ["book-authors"], queryFn: libraryService.getAuthors, enabled: canManage });
  const categories = useQuery({ queryKey: ["book-categories"], queryFn: libraryService.getCategories, enabled: canManage });
  const refresh = () =>
    Promise.all(
      ["book-categories", "book-authors", "library-books", "book-copies", "library-borrowings", "library-reservations", "library-fines", "library-stats", "library-programs", "library-progress"].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    );
  const select = (next: Tab) => {
    setOpenBook(null);
    setParams(next === "catalogue" ? {} : { tab: next }, { replace: true });
  };

  const now = Date.now();
  const loanList = loans.data ?? [];
  const out = loanList.filter(isOut).length;
  const late = loanList.filter((loan) => isLate(loan, now)).length;
  const onShelf = (books.data ?? []).reduce((sum, book) => sum + book.available_copies, 0);
  const allCopies = (books.data ?? []).reduce((sum, book) => sum + book.total_copies, 0);
  const unpaid = unpaidTotal(fines.data ?? []);
  const waiting = (reservations.data ?? []).filter(isOpenReservation).length;
  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof BookOpen; alert?: boolean }[] = [
    { label: "Titles", value: String(books.data?.length ?? "–"), note: `${onShelf} of ${allCopies} copies on the shelf`, tab: "catalogue", icon: LibraryIcon },
    { label: canManage ? "Out on loan" : "Books out", value: String(out), note: waiting ? `${waiting} ${waiting === 1 ? "reservation" : "reservations"} waiting` : "No reservations waiting", tab: "loans", icon: Users },
    { label: "Overdue", value: String(late), note: late ? "Past their due date" : "Everything is on time", tab: "loans", icon: Clock, alert: late > 0 },
    { label: "Unpaid fines", value: money(unpaid), note: `${(fines.data ?? []).filter((fine) => fine.status === "pending").length} unpaid`, tab: "fines", icon: Coins, alert: unpaid > 0 },
  ];
  const firstRun = canManage && !books.isLoading && !books.isError && !books.data?.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Library</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "The catalogue, lending and returns, reservations, fines and reading programmes."
            : hasRole("parent")
              ? "Find books, see what your children have borrowed, and reserve what they want to read next."
              : "Find a book, see what you have borrowed, and reserve what you want to read next."}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      {firstRun && (
        <section className="rounded-lg border border-primary-200 bg-primary-50 p-4">
          <h2 className="text-sm font-semibold text-gray-900">Getting the library started</h2>
          <ol className="mt-2 grid gap-2 text-sm text-gray-700 sm:grid-cols-3">
            <li>1. Add each title with <strong>New title</strong>, with its authors, category, shelf and how many copies you hold.</li>
            <li>2. Stick a barcode in each copy and record it on the copy, so it can be found by scanning.</li>
            <li>3. Lend books from <strong>Loans</strong>. Returns, renewals and late fines follow from there.</li>
          </ol>
        </section>
      )}

      <nav role="tablist" aria-label="Library sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = item.id === "loans" && late ? late : item.id === "reservations" && waiting ? waiting : undefined;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {canManage ? item.staff : item.reader}
              {badge !== undefined && (
                <span className={`rounded-full px-1.5 py-0.5 text-xs tabular-nums ${item.id === "loans" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      {tab === "catalogue" && (
        <CataloguePanel
          key={openBook ?? "catalogue"}
          openId={openBook}
          books={books.data}
          copies={copies.data ?? []}
          loans={loanList}
          reservations={reservations.data ?? []}
          authors={authors.data ?? []}
          categories={categories.data ?? []}
          students={students.data ?? []}
          isLoading={books.isLoading}
          isError={books.isError}
          onRetry={() => books.refetch()}
          canManage={canManage}
          canReserve={canReserve}
          onChanged={refresh}
        />
      )}
      {tab === "copies" && (
        <CopiesPanel
          copies={copies.data}
          isLoading={copies.isLoading}
          onOpenTitle={(bookId) => {
            setParams({}, { replace: true });
            setOpenBook(bookId);
          }}
        />
      )}
      {tab === "loans" && (
        <LoansPanel
          loans={loans.data}
          copies={copies.data ?? []}
          students={students.data ?? []}
          reservations={reservations.data ?? []}
          isLoading={loans.isLoading}
          isError={loans.isError}
          onRetry={() => loans.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "reservations" && (
        <ReservationsPanel
          reservations={reservations.data}
          books={books.data ?? []}
          students={students.data ?? []}
          isLoading={reservations.isLoading}
          isError={reservations.isError}
          onRetry={() => reservations.refetch()}
          canManage={canManage}
          canReserve={canReserve}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "fines" && (
        <FinesPanel fines={fines.data} isLoading={fines.isLoading} isError={fines.isError} onRetry={() => fines.refetch()} canManage={canManage} canWaive={canWaive} onChanged={refresh} initialView={view} />
      )}
      {tab === "programmes" && (
        <ProgramsPanel
          programs={programs.data}
          progress={progress.data ?? []}
          students={students.data ?? []}
          isLoading={programs.isLoading}
          isError={programs.isError}
          onRetry={() => programs.refetch()}
          canManage={canManage}
          onChanged={refresh}
        />
      )}
      {tab === "setup" && canManage && (
        <AuthorsCategoriesPanel books={books.data ?? []} authors={authors.data} categories={categories.data} isLoading={authors.isLoading || categories.isLoading} onChanged={refresh} />
      )}
    </div>
  );
}
